/**
 * App Registry — the "plug in" mechanism.
 *
 * To add a new micro-frontend:
 *   1. Add an entry to APP_REGISTRY below
 *   2. Add a matching remote entry in vite.config.ts
 *   3. That's it!
 */

import type { ComponentType } from "react";

export interface NavItem {
  /** Display label in the top nav */
  label: string;
  /** Route path relative to the app's basePath (e.g. "runs" → /ai-manager/runs) */
  path: string;
  /** Icon name from lucide-react (resolved in the component) */
  icon?: string;
  /** Role required to see this nav item. Empty = everyone with app access. */
  requiredRole?: string;
}

export interface SubNavItem {
  label: string;
  path: string;
  icon?: string;
}

export interface NavSection {
  /** The primary nav items that this section covers */
  matchPaths: string[];
  /** Sub-navigation tabs shown below the top bar when this section is active */
  tabs: SubNavItem[];
}

export interface NavManifest {
  navItems: NavItem[];
  subNavSections: NavSection[];
}

export interface AppConfig {
  /** Unique key — must match the remote name in vite.config.ts (underscores, not hyphens) */
  id: string;
  /** Display name shown in the shell sidebar */
  name: string;
  /** Short label for the sidebar */
  shortName: string;
  /** Route prefix in the shell (e.g. "/ai-manager") */
  basePath: string;
  /** Cognito role names required to see this app. Empty array = all authenticated users. */
  requiredRoles: string[];
  /** Description shown as tooltip */
  description: string;
  /** Icon identifier for the sidebar */
  icon: string;
  /** Primary navigation items shown in the top bar */
  navItems?: NavItem[];
  /** Sub-navigation sections with tabs */
  subNavSections?: NavSection[];
  /** Default route within the app (relative to basePath) */
  defaultRoute: string;
}

export const APP_REGISTRY: AppConfig[] = [
  {
    id: "ingestion_manager",
    name: "Ingestion Manager",
    shortName: "Ingest",
    basePath: "/ingestion-manager",
    requiredRoles: [],
    description: "Configure and monitor data ingestion pipelines",
    icon: "Database",
    defaultRoute: "dashboard",
  },
  {
    id: "cohort_dashboard",
    name: "Cohort Dashboard",
    shortName: "Cohort",
    basePath: "/cohort-dashboard",
    requiredRoles: [],
    description: "Data quality and cohort profiling dashboard",
    icon: "Users",
    defaultRoute: "",
  },
  {
    id: "ai_manager",
    name: "AI Manager",
    shortName: "AI",
    basePath: "/ai-manager",
    requiredRoles: [],
    description: "Manage AI runs, evaluations, and results",
    icon: "Brain",
    defaultRoute: "",
    navItems: [
      { label: "Dashboard", path: "", icon: "LayoutDashboard" },
      { label: "Runs", path: "runs", icon: "Play", requiredRole: "ai_manager_run" },
      { label: "Results", path: "results", icon: "FileText" },
      { label: "Evaluations", path: "evaluations", icon: "ClipboardCheck" },
      { label: "Admin", path: "admin", icon: "Shield", requiredRole: "ai_manager_admin" },
    ],
    subNavSections: [],
  },
  {
    id: "model_dashboard",
    name: "Model Dashboard",
    shortName: "Models",
    basePath: "/model-dashboard",
    requiredRoles: [],
    description: "Model performance and monitoring dashboard",
    icon: "Layers",
    defaultRoute: "",
    subNavSections: [],
  }
];
